import { createComment, deleteReaction, updateComment } from '@gik/api/activities/reactions';
import { useInkind } from '@gik/api/inkinds/inkind';
import type { ReactionLookupAttribute, UseStreamSWROptions } from '@gik/api/stream/useStream';
import { useComment } from '@gik/api/stream/useStream';
import { useUser } from '@gik/api/users/user';
import { SignInFlowCalloutBlock } from '@gik/auth/components/SignInFlow/SignInFlowCallout';
import { SignInFlowContentCopyVariant } from '@gik/auth/components/SignInFlow/SignInFlowStartContent';
import { signin } from '@gik/auth/utils/auth';
import { timeoutDefaultValue } from '@gik/core/constants';
import { useDevStore } from '@gik/core/store/DevStore';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { isTouchDevice } from '@gik/core/utils/browser';
import { scrollIntoView } from '@gik/core/utils/scroll';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import type { Reaction, StreamComment } from '@gik/stream/types/Stream';
import StreamClient from '@gik/stream/utils/StreamClient';
import { Button } from '@gik/ui/Button';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import type { ReactionEmojiKey } from '@gik/ui/OpenMoji';
import React from 'react';
import type {
  StreamPaginationAddItemCallback,
  StreamPaginationDeleteItemCallback,
  StreamPaginationLoadLatestItemsCallback,
  StreamPaginationOrder,
  StreamPaginationReloadItemCallback,
  StreamPaginationRenderCallbackProps,
  StreamPaginationSetErrorCallback,
  StreamPaginationUpdateItemCallback,
} from '../Posts/StreamPagination';
import { StreamPagination } from '../Posts/StreamPagination';
import type { StreamReactionsContextProps } from '../Posts/StreamReactions';
import { StreamReactionsContext } from '../Posts/StreamReactions';
import { Comment } from './Comment';
import { CommentInputWithAvatar } from './CommentInputWithAvatar';
import { CommentsList } from './CommentsList';

export type StreamCommentsProps<D> = {
  forId: string;
  highlightedId?: string;
  isPageOwnerOrOrganizer?: boolean;
  parentIsReaction?: boolean;
  inputAtTop?: boolean;
  showTotal?: boolean;
  shouldReload?: boolean;
  newerIsReversed?: boolean;
  disabled?: boolean;
  initialTotal?: number;
  layout?: string;
  order?: StreamPaginationOrder;
  parentReplyingId?: string;
  parentReplyingUserId?: string;
  latest_comments?: D[];
  onDeleteComment?: (id: string) => void;
  onAddComment?: (forId: string, value: string) => void;
  onBeforeAddComment?: (forId: string, value: string) => void;
  onReply?: (comment: D, subComment?: D) => void;
  onReaction?: (forId: string, name: ReactionEmojiKey) => void;
  onDeleteReaction?: (forId: string, name: ReactionEmojiKey) => void;
  onCancelReply?: () => void;
  onChange?: (items: D[], total: number) => void;
  onSubCommentsChange?: (items: D[], total: number) => void;
} & UIComponent;

export type renderCommentProps<D> = {
  comment: D;
  items: D[];
  error: string;
  setError: StreamPaginationSetErrorCallback;
  // loadNewestItems: StreamPaginationLoadNewerItemsCallback<D[]>;
  addItem: StreamPaginationAddItemCallback;
  reloadItem: StreamPaginationReloadItemCallback;
  updateItem: StreamPaginationUpdateItemCallback;
  deleteItem: StreamPaginationDeleteItemCallback;
  loadLatestItems: StreamPaginationLoadLatestItemsCallback;
};

type D = Reaction<StreamComment>;

function StreamCommentsComp({
  forId,
  highlightedId,
  children,
  className,
  newerIsReversed,
  isPageOwnerOrOrganizer,
  parentIsReaction,
  inputAtTop,
  layout,
  order = 'desc',
  showTotal,
  parentReplyingId,
  parentReplyingUserId,
  latest_comments,
  initialTotal,
  shouldReload,
  disabled,
  onAddComment,
  onBeforeAddComment,
  onDeleteComment,
  onReply,
  onReaction,
  onDeleteReaction,
  onCancelReply,
  onChange,
  onSubCommentsChange,
  ...otherProps
}: React.PropsWithChildren<StreamCommentsProps<D>>): React.ReactElement {
  const bem = useBemCN('stream-comments');

  const context = React.useContext<StreamReactionsContextProps>(StreamReactionsContext);
  const participants = context?.participants;
  const commentParticipants = context?.commentParticipants;
  const replyingId = context?.replyingId;
  const setReplyingId = context?.setReplyingId;
  const replyMessage = context?.replyMessage;
  const setReplyMessage = context?.setReplyMessage;
  const replyingUserId = context?.replyingUserId;
  const setReplyingUserId = context?.setReplyingUserId;
  const inkindRouteId = useInkindStore(store => store.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);
  const canEdit = useInkindCan('manage', inkindRouteId, inkind?.groupId);

  const streamDebug = useDevStore(state => state.streamDebug);

  const allParticipants = participants
    .concat(commentParticipants || [])
    // filter unique ids
    .filter((obj, index, arr) => {
      return arr.findIndex(t => t.id === obj.id) === index;
    });

  const replyingUserIdRef = React.useRef<string>();
  replyingUserIdRef.current = replyingUserId;

  const [creating, setCreating] = React.useState<boolean>(false);
  const [localReplyMessage, setLocalReplyMessage] = React.useState<string>();

  // const [initialFetch, setInitialFetch] = React.useState<boolean>(!!latest_comments);
  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);

  // if there is a highlightedId then we need to fetch the comment first in order to know if it is a subcomment or not
  const { data: highlightedComment } = useComment(highlightedId);

  const feedType: ReactionLookupAttribute = parentIsReaction ? 'reaction' : 'activity';

  const handleCancelReply = React.useCallback(() => {
    setReplyMessage('');
    setLocalReplyMessage('');
    setReplyingId(null);
    setReplyingUserId(null);
    onCancelReply?.();
  }, [onCancelReply, setReplyingId, setReplyingUserId, setReplyMessage]);

  const handleReply = React.useCallback(
    (comment: D, subComment?: D) => {
      const target = subComment ? subComment : comment;
      const userId = target?.data.userId as string;

      setReplyingUserId(userId);

      const allParticipants = participants?.concat(commentParticipants || []);
      const userName = allParticipants.find(user => user.id === userId)?.display as string;

      setReplyMessage(`@[${userName}](${userId}) `);
      replyingUserIdRef.current = userId;

      setReplyingId(comment.id);
    },
    [participants, commentParticipants, setReplyMessage, setReplyingId, setReplyingUserId]
  );

  const handleBeforeAddComment = React.useCallback(() => {
    setReplyMessage('');
    setLocalReplyMessage('');
    setReplyingId(null);
    setReplyingUserId(null);
  }, [setReplyingId, setReplyingUserId, setReplyMessage]);

  const loginWarning = React.useCallback(async () => {
    if (!userId) {
      await signin({
        copyVariant: !inkindRouteId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
        callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
        routeId: inkindRouteId,
      });
    }
  }, [inkindRouteId, userId]);

  const createNewComment = React.useCallback(
    async (
      forId: string,
      value: string,
      loadLatestItems: StreamPaginationLoadLatestItemsCallback,
      setError: StreamPaginationSetErrorCallback
    ) => {
      setCreating(true);
      setError(`new-${forId}`, undefined);

      const response = await createComment(forId, value, parentIsReaction);
      if (response) {
        await loadLatestItems();
      } else {
        setError(`new-${forId}`, 'create_fail');
      }
      setReplyMessage('');
      setLocalReplyMessage('');
      setCreating(false);
    },
    [parentIsReaction, setReplyMessage]
  );

  const handleAddComment = React.useCallback(
    async (
      forId: string,
      value: string,
      addItem: StreamPaginationAddItemCallback,
      loadLatestItems: StreamPaginationLoadLatestItemsCallback,
      setError: StreamPaginationSetErrorCallback
    ) => {
      const elId = `new-${forId}`;

      onBeforeAddComment?.(forId, value);

      setReplyMessage('');
      setLocalReplyMessage('');
      setReplyingId(null);

      // optimistically create item
      addItem({
        id: elId,
        data: {
          message: value,
          userAvatarThumbMediumURL: user.avatarThumbMediumURL,
          userAvatarThumbSmallURL: user.avatarThumbSmallURL,
          userFullName: user.fullName,
          userId: user.id,
        },
      });

      // scroll new comment into view
      setTimeout(() => {
        const el = document.querySelector(`[data-id='${elId}']`) as HTMLDivElement;
        if (el) scrollIntoView(el, { offset: -20, useBoundingRect: true });
      }, timeoutDefaultValue);

      await createNewComment(forId, value, loadLatestItems, setError);
      onAddComment?.(forId, value);
    },
    [createNewComment, onBeforeAddComment, onAddComment, setReplyingId, setReplyMessage, user]
  );

  const renderInput = ({
    addItem,
    loadLatestItems: loadNewestItems,
    setError,
  }: StreamPaginationRenderCallbackProps<D[]>) => {
    return (
      <>
        {(!parentIsReaction || (parentReplyingId && replyingId && parentReplyingId === replyingId)) && (
          <>
            <CommentInputWithAvatar
              autoFocus={replyingId && parentReplyingId === replyingId}
              disabled={disabled || creating}
              variant="neutral"
              placeholder="Comment"
              value={parentReplyingId === replyingId ? replyMessage : localReplyMessage}
              onClick={loginWarning}
              onCancel={parentReplyingId ? handleCancelReply : undefined}
              onChange={({ target }) => {
                parentReplyingId === replyingId ? setReplyMessage(target.value) : setLocalReplyMessage(target.value);
              }}
              users={allParticipants}
              forId={forId}
              submitButton={isTouchDevice()}
              submitOnEnter={!isTouchDevice()}
              onSubmit={(...args) => handleAddComment(...args, addItem, loadNewestItems, setError)}
              escapeMessage={parentReplyingId && parentReplyingId === replyingId}
            />
          </>
        )}
      </>
    );
  };

  // ----- Render functions

  // function setReplyMessage(targetId: string, userFullName: string, userId: string) {
  //   const initialMessage = `@[${userFullName}](${userId}) `;
  //   setValue(initialMessage);
  //   document.getElementById(`${targetId}-input`)?.focus();
  // }

  function renderComment({
    comment,
    items,
    error,
    reloadItem,
    addItem,
    updateItem,
    deleteItem,
    loadLatestItems,
    setError,
  }: renderCommentProps<D>) {
    if (!comment) return null;

    const latest_comment_match = latest_comments?.find(item => item?.id === comment.id);
    const allItems_match = items?.find(item => item?.id === comment.id);

    const match = latest_comment_match || allItems_match;

    // eslint-disable-next-line
    let subComments = match ? (match?.latest_children?.comment as any as D[]) || [] : undefined;

    // if (subComments && !newerIsReversed) subComments = subComments.reverse();

    async function handleUpdate(id: string, value: string) {
      // optimistically update item
      updateItem(id, {
        data: {
          message: value,
        },
      });

      // scroll edited comment into view
      setTimeout(() => {
        const elId = `comment-${id}`;
        const el = document.getElementById(elId);
        if (el) scrollIntoView(el, { offset: -20, useBoundingRect: true });
      }, 100);

      updateSubmit(id, value);
    }

    async function updateSubmit(id: string, value: string) {
      setError(id, undefined);
      const response = await updateComment(id, value);

      if (response) {
        reloadItem(id);
      } else {
        setError(id, 'update_fail');
      }
    }

    async function handleDelete(id: string) {
      setError(id, undefined);
      const response = await deleteReaction(id, canEdit ? inkindRouteId : undefined);

      // delete item without reloading
      if (response) {
        deleteItem(id);
      } else {
        setError(id, 'delete_fail');
      }
    }

    return (
      <div key={`${comment.id}`}>
        <Comment
          data-id={comment.id}
          highlighted={comment.id === highlightedId}
          disabled={comment.id.startsWith('new')}
          key={comment.data.message as string}
          comment={comment}
          isPageOwnerOrOrganizer={isPageOwnerOrOrganizer}
          users={context?.participants}
          onReply={() => {
            return parentIsReaction ? onReply(comment) : handleReply(comment);
          }}
          parentIsReaction={parentIsReaction}
          onReaction={reloadItem}
          onDeleteReaction={reloadItem}
          onDelete={handleDelete}
          onEditSubmit={handleUpdate}
          error={error}
          onCreateRetry={(id: string, value: string) => createNewComment(id, value, loadLatestItems, setError)}
          onUpdateRetry={(id: string, value: string) => updateSubmit(id, value)}
          onDeleteRetry={(id: string) => handleDelete(id)}
        />

        {!parentIsReaction && (
          <StreamComments
            // newerIsReversed
            // order="asc"
            highlightedId={highlightedComment?.parent === '' ? undefined : highlightedId}
            forId={comment.id}
            isPageOwnerOrOrganizer={isPageOwnerOrOrganizer}
            initialTotal={match ? match?.children_counts?.comment || 0 : undefined}
            onReaction={reloadItem}
            latest_comments={!highlightedId ? subComments : undefined}
            onDeleteReaction={reloadItem}
            onReply={c => handleReply(comment, c)}
            parentIsReaction={true}
            parentReplyingId={replyingId === comment.id ? replyingId : undefined}
            // parentReplyingUserId={replyingId === comment.id ? comment.data.userId : undefined}
            // parentReplyingUserFullName={
            //   (context?.participants.find(user => user.id === comment.data.userId)?.display as string) ||
            //   (context?.commentParticipants.find(user => user.id === comment.data.userId)?.display as string)
            // }
            parentReplyingUserId={comment.data.userId as string}
            onCancelReply={() => handleCancelReply()}
            onBeforeAddComment={handleBeforeAddComment}
            onChange={onSubCommentsChange}
          />
        )}

        {/* {!isReaction && replyingId === comment?.id && (
          <CommentInputWithAvatar
            forId={comment.id}
            variant="neutral"
            placeholder="Comment"
            value={value2}
            users={context?.participants}
            onCancel={handleCancelReply}
            onChange={({ target }) => {
              setValue2(target.value);
            }}
            onSubmit={(...args) => handleAddComment(...args, addItem, loadNewerItems, setError)}
          />
        )} */}
      </div>
    );
  }

  // React.useEffect(() => {
  //   if (!parentReplyingId || parentReplyingId === replyingId) return;

  //   const allParticipants = participants?.concat(commentParticipants || []);

  //   // const replyingUserId = latestReplyingUserId.current;

  //   const userName = allParticipants.find(user => user.id === replyingUserId)?.display as string;

  //   setReplyMessage(parentReplyingId, userName, parentReplyingUserId);
  //   setReplyingId(parentReplyingId);
  // }, [
  //   parentReplyingId,
  //   parentReplyingUserId,
  //   replyingId,
  //   replyingUserId,
  //   latestReplyingUserId,
  //   participants,
  //   commentParticipants,
  // ]);

  if (highlightedId && !highlightedComment) return <LoadingSpinner center />;

  return (
    <div {...bem(null, [{ ['no-interaction']: !userId }], className)} {...otherProps}>
      <StreamPagination<D>
        debug={streamDebug}
        verbose={streamDebug}
        hasNextButton
        {...bem('pagination', [{ top: !parentIsReaction }])}
        // hasPrev={!parentIsReaction}
        // verbose={!parentIsReaction}
        // debug={!parentIsReaction}
        highlightedId={
          parentIsReaction
            ? highlightedId
            : highlightedComment?.parent === ''
            ? highlightedId
            : highlightedComment?.parent
        }
        initialData={latest_comments}
        initialTotal={initialTotal}
        showTotal={showTotal}
        order={order}
        newerIsReversed={newerIsReversed}
        onChange={onChange}
        // onReady={() => setInitialFetch(false)}
        // onChange={handleStreamChange}
        sanitizeData={(items: D[]) => {
          return items?.filter(item => !item.data.deletedTime);
        }}
        fetch={function useFetch(paginationOptions: UseStreamSWROptions) {
          // don't fetch if this comment was attached to a temporary new (optimistically created) post
          if (forId?.startsWith('new')) return null;
          return StreamClient.getComments(feedType, forId, { ...paginationOptions, recentReactionsLimit: 5 });
        }}
        fetchSingle={function useFetch(id: string) {
          return useComment(id);
        }}
        nextButton={({ isLoading, onClick, total, currentPos }) => {
          return (
            <>
              <Button variant="white" size="xs" pill onClick={onClick} disabled={isLoading} loading={isLoading}>
                Show More&nbsp;
                {showTotal && (
                  <span>
                    ({currentPos}/{total})
                  </span>
                )}
              </Button>
              {/* {loadMoreError && <div {...bem('load-more-error')}>Failed to get more comments</div>} */}
            </>
          );
        }}
        prevButton={({ isLoading, onClick }) => {
          return (
            <>
              <Button variant="white" size="xs" pill onClick={onClick} disabled={isLoading} loading={isLoading}>
                Show older comments&nbsp;
              </Button>
            </>
          );
        }}
        header={(...args) => (inputAtTop ? renderInput(...args) : null)}
        footer={(...args) => (inputAtTop ? null : renderInput(...args))}
      >
        {({
          data: items,
          reloadItem,
          loadLatestItems,
          addItem,
          updateItem,
          deleteItem,
          errors,
          setError,
          cursorPos,
        }) => {
          let pos = 0;

          return (
            <CommentsList>
              {items?.map(comment => {
                if (pos >= cursorPos) return null;
                pos++;
                return renderComment({
                  comment,
                  items,
                  error: errors[comment.id] || errors['new-' + comment.id],
                  reloadItem,
                  addItem,
                  updateItem,
                  deleteItem,
                  loadLatestItems,
                  setError,
                });
              })}
            </CommentsList>
          );
        }}
      </StreamPagination>
    </div>
  );
}

export const StreamComments = withComponentErrorBoundary(StreamCommentsComp);
